import { isPresent } from "@/lib/utils"
import { PROVIDERS, Provider } from "@/lib/video-utils"
import { NativeBridge } from "@/sdk/native-bridge"

export type CastVideo = {
  id?: string | number | null
  provider: Provider
  src?: string | null
  title?: string | null
  poster?: string | null
}

export type MvtCastPlayer = {
  paused: boolean
  currentTime: number
  duration: number
  play(): unknown
  pause(): unknown
}

export type CastError = Error & { code?: string }

export type CastAttachOptions = {
  video: CastVideo
  localPlayer: () => MvtCastPlayer | null
  onError?: (error: CastError) => void
}

const CASTABLE: Partial<Record<Provider, (video: CastVideo) => boolean>> = {
  [PROVIDERS.MUX]: video => !!video.src,
  [PROVIDERS.VIMEO]: video => isPresent(video.id)
}

export type CastBridge = Pick<NativeBridge, "available" | "invoke" | "on">

export type CastState = {
  supported: boolean
  available: boolean
  casting: boolean
  connecting: boolean
  deviceName: string | null
}

export type CastSessionHandle = {
  readonly state: CastState
  showPicker(): Promise<void>
  stop(): void
  detach(): void
}

export class Cast {
  constructor(
    private readonly bridge: CastBridge,
    private readonly providers: () => string[],
    private readonly onChange: () => void
  ) {}

  isCastable(video: CastVideo): boolean {
    return (
      this.bridge.available &&
      this.providers().includes(video.provider) &&
      !!CASTABLE[video.provider]?.(video)
    )
  }

  // The session code loads behind the handle, so only apps that cast download it.
  attach(options: CastAttachOptions): CastSessionHandle {
    const state: CastState = {
      supported: false,
      available: false,
      casting: false,
      connecting: false,
      deviceName: null
    }

    let session: CastSessionHandle | undefined
    let detached = false

    import(/* webpackChunkName: "cast-session" */ "@/sdk/cast/session")
      .then(({ CastSession }) => {
        if (detached) return

        const loaded = new CastSession({
          ...options,
          state,
          bridge: this.bridge,
          onChange: this.onChange
        })

        session = loaded
        loaded.start()
      })
      .catch(error => console.error("Cast session failed to load:", error))

    return {
      state,
      showPicker: async () => session?.showPicker(),
      stop: () => session?.stop(),
      detach: () => {
        detached = true
        session?.detach()
      }
    }
  }
}
